import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Trash2, Loader2, StickyNote } from "lucide-react";
import {
  listStickyNotes, createStickyNote, updateStickyNote, setStickyStatus, deleteStickyNote,
  STICKY_COLORS, colorOf, STICKY_STATUSES, statusLabel, isClosed,
} from "@/lib/stickyNotes";

const TABS = [
  { v: "all", t: "All" },
  { v: "active", t: "Active" },
  { v: "closed", t: "Closed" },
];

// One editable coloured card.
function NoteCard({ note, onSaveText, onRecolor, onStatus, onDelete, busy }) {
  const c = colorOf(note.color_tag);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.note_text || "");
  useEffect(() => { setText(note.note_text || ""); }, [note.note_text]);

  const commit = () => {
    const t = text.trim();
    setEditing(false);
    if (t && t !== note.note_text) onSaveText(note, t);
    else setText(note.note_text || "");
  };

  return (
    <div data-testid={`sticky-card-${note.note_id}`} className="rounded-[12px] p-3" style={{ background: c.bg, border: `1px solid ${c.br}` }}>
      {editing ? (
        <textarea
          autoFocus value={text} onChange={(e) => setText(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit(); if (e.key === "Escape") { setText(note.note_text || ""); setEditing(false); } }}
          data-testid={`sticky-edit-${note.note_id}`}
          className="w-full resize-none bg-transparent outline-none text-[13px] leading-[1.5]"
          style={{ color: c.ink, minHeight: 54 }}
        />
      ) : (
        <div role="button" tabIndex={0} onClick={() => setEditing(true)} onKeyDown={(e) => e.key === "Enter" && setEditing(true)}
          data-testid={`sticky-text-${note.note_id}`}
          className="text-[13px] leading-[1.5] whitespace-pre-wrap cursor-text break-words" style={{ color: c.ink }}>
          {note.note_text}
        </div>
      )}

      <div className="flex items-center gap-1.5 mt-2.5">
        {STICKY_COLORS.map((sc) => (
          <button key={sc.tag} onClick={() => onRecolor(note, sc.tag)} aria-label={`Colour ${sc.tag}`} title={sc.tag}
            data-testid={`sticky-color-${note.note_id}-${sc.tag}`}
            className="w-[15px] h-[15px] rounded-full transition-transform hover:scale-110"
            style={{ background: sc.bg, border: note.color_tag === sc.tag ? `2px solid ${sc.ink}` : `1px solid ${sc.br}` }} />
        ))}
        <span className="flex-1" />
        <button onClick={() => onDelete(note)} aria-label="Delete note" data-testid={`sticky-delete-${note.note_id}`}
          className="w-[26px] h-[26px] rounded-[7px] flex items-center justify-center hover:bg-black/10" style={{ color: c.ink }}>
          <Trash2 size={13} />
        </button>
      </div>

      <div className="flex items-center justify-between mt-2 pt-2" style={{ borderTop: `1px solid ${c.br}` }}>
        <span className="text-[10.5px] truncate" style={{ color: c.ink, opacity: 0.75 }} title={note.created_by || ""}>
          {note.created_by ? note.created_by.split("@")[0] : "—"}
        </span>
        <div className="flex items-center gap-1.5">
          {busy ? <Loader2 size={12} className="animate-spin" style={{ color: c.ink }} /> : null}
          <select value={note.status} onChange={(e) => onStatus(note, e.target.value)} data-testid={`sticky-status-${note.note_id}`}
            className="text-[11px] font-semibold rounded-[7px] px-1.5 py-1 outline-none cursor-pointer"
            style={{ color: c.ink, background: "rgba(255,255,255,.55)", border: `1px solid ${c.br}` }}>
            {STICKY_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.t}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

export default function StickyNotesPanel({ clientId, chartId, chartUuid, patientName, onClose, onActiveCount }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftColor, setDraftColor] = useState("yellow");
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const addRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listStickyNotes(chartUuid); setNotes(r.notes || []); }
    catch { toast.error("Couldn't load sticky notes"); }
    finally { setLoading(false); }
  }, [chartUuid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const onEsc = (e) => e.key === "Escape" && !adding && onClose(); window.addEventListener("keydown", onEsc); return () => window.removeEventListener("keydown", onEsc); }, [onClose, adding]);

  const shown = useMemo(() => notes.filter((n) => tab === "all" ? true : tab === "closed" ? isClosed(n.status) : !isClosed(n.status)), [notes, tab]);
  const counts = useMemo(() => ({
    all: notes.length,
    active: notes.filter((n) => !isClosed(n.status)).length,
    closed: notes.filter((n) => isClosed(n.status)).length,
  }), [notes]);
  useEffect(() => { if (!loading && onActiveCount) onActiveCount(counts.active); }, [counts.active, loading, onActiveCount]);

  const doCreate = async () => {
    const t = draft.trim(); if (!t) return;
    setCreating(true);
    try {
      const note = await createStickyNote({ client_id: clientId, chart_id: chartId, chart_uuid: chartUuid, patient_name: patientName || undefined, note_text: t, color_tag: draftColor });
      setNotes((p) => [note, ...p]);
      setDraft(""); setAdding(false);
      toast.success("Note added");
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Couldn't add the note"); }
    finally { setCreating(false); }
  };

  const patch = (id, p) => setNotes((all) => all.map((n) => (n.note_id === id ? { ...n, ...p } : n)));

  const onSaveText = async (note, text) => {
    patch(note.note_id, { note_text: text }); setSavingId(note.note_id);
    try { await updateStickyNote(note.note_id, { note_text: text }); }
    catch { toast.error("Couldn't save the note"); load(); }
    finally { setSavingId(null); }
  };
  const onRecolor = async (note, tag) => {
    if (tag === note.color_tag) return;
    patch(note.note_id, { color_tag: tag }); setSavingId(note.note_id);
    try { await updateStickyNote(note.note_id, { note_text: note.note_text, color_tag: tag }); }
    catch { toast.error("Couldn't recolour"); load(); }
    finally { setSavingId(null); }
  };
  const onStatus = async (note, status) => {
    patch(note.note_id, { status }); setSavingId(note.note_id);
    try { await setStickyStatus(note.note_id, status); toast.success(`Marked ${statusLabel(status)}`); }
    catch { toast.error("Couldn't change status"); load(); }
    finally { setSavingId(null); }
  };
  const onDelete = async (note) => {
    setNotes((p) => p.filter((n) => n.note_id !== note.note_id));
    try { await deleteStickyNote(note.note_id); toast.success("Note deleted"); }
    catch { toast.error("Couldn't delete"); load(); }
  };

  return (
    <div data-testid="sticky-panel" style={{ position: "fixed", inset: 0, zIndex: 4200, background: "rgba(20,20,18,.32)", display: "flex", justifyContent: "flex-end", animation: "stkfade .16s ease" }} onMouseDown={onClose}>
      <aside onMouseDown={(e) => e.stopPropagation()} style={{ width: "min(420px,96vw)", height: "100%", background: "#FBFBFA", boxShadow: "-16px 0 50px -20px rgba(20,20,18,.45)", display: "flex", flexDirection: "column", animation: "stkslide .22s cubic-bezier(.4,0,.2,1)" }}>
        <div className="flex items-center gap-2 px-4 py-3.5" style={{ borderBottom: "1px solid #ECECEF" }}>
          <StickyNote size={17} style={{ color: "#92650B" }} />
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-bold leading-tight" style={{ color: "#141412" }}>Sticky notes</div>
            {patientName ? <div className="text-[11.5px] truncate" style={{ color: "#8A8A86" }}>{patientName}</div> : null}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="sticky-close" className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center hover:bg-black/[.05]" style={{ color: "#6C6C69" }}><X size={16} /></button>
        </div>

        <div className="flex items-center gap-1 px-4 pt-3">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`sticky-tab-${t.v}`}
              className="text-[12.5px] font-semibold px-3 py-1.5 rounded-[9px] transition-colors"
              style={{ background: tab === t.v ? "#141412" : "transparent", color: tab === t.v ? "#fff" : "#6C6C69" }}>
              {t.t}<span className="ml-1 tabular-nums" style={{ opacity: 0.7 }}>{counts[t.v] ?? 0}</span>
            </button>
          ))}
          <span className="flex-1" />
          <button onClick={() => { setAdding((a) => !a); setTimeout(() => addRef.current?.focus(), 30); }} data-testid="sticky-add-toggle"
            className="inline-flex items-center gap-1 text-[12.5px] font-semibold px-3 py-1.5 rounded-[9px] text-white" style={{ background: "#4B4BE8" }}>
            <Plus size={14} /> Add
          </button>
        </div>

        {adding ? (
          <div className="mx-4 mt-3 rounded-[12px] p-3" style={{ background: colorOf(draftColor).bg, border: `1px solid ${colorOf(draftColor).br}` }}>
            <textarea ref={addRef} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type a note for this patient…" maxLength={5000}
              data-testid="sticky-add-input" className="w-full resize-none bg-transparent outline-none text-[13px] leading-[1.5]" style={{ color: colorOf(draftColor).ink, minHeight: 60 }}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) doCreate(); }} />
            <div className="flex items-center gap-1.5 mt-2">
              {STICKY_COLORS.map((sc) => (
                <button key={sc.tag} onClick={() => setDraftColor(sc.tag)} aria-label={`Colour ${sc.tag}`} data-testid={`sticky-add-color-${sc.tag}`}
                  className="w-[16px] h-[16px] rounded-full transition-transform hover:scale-110"
                  style={{ background: sc.bg, border: draftColor === sc.tag ? `2px solid ${sc.ink}` : `1px solid ${sc.br}` }} />
              ))}
              <span className="flex-1" />
              <button onClick={() => { setAdding(false); setDraft(""); }} className="text-[12px] font-semibold px-2.5 py-1 rounded-[7px]" style={{ color: "#6C6C69" }}>Cancel</button>
              <button onClick={doCreate} disabled={!draft.trim() || creating} data-testid="sticky-add-save"
                className="inline-flex items-center gap-1 text-[12px] font-bold px-3 py-1 rounded-[7px] text-white disabled:opacity-40" style={{ background: "#141412" }}>
                {creating ? <Loader2 size={12} className="animate-spin" /> : null} Save
              </button>
            </div>
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
          {loading ? (
            <div className="py-10 text-center text-[13px]" style={{ color: "#9B9B97" }}>Loading…</div>
          ) : shown.length === 0 ? (
            <div className="py-12 text-center">
              <div className="text-[13px] font-semibold" style={{ color: "#141412" }}>{tab === "all" ? "No sticky notes yet" : tab === "closed" ? "Nothing closed" : "Nothing active"}</div>
              <div className="text-[12px] mt-1" style={{ color: "#9B9B97" }}>{tab === "all" ? "Add a coloured note to flag a follow-up for this patient." : "Notes you mark here will show up in this tab."}</div>
            </div>
          ) : (
            shown.map((n) => (
              <NoteCard key={n.note_id} note={n} busy={savingId === n.note_id}
                onSaveText={onSaveText} onRecolor={onRecolor} onStatus={onStatus} onDelete={onDelete} />
            ))
          )}
        </div>
      </aside>
      <style>{`@keyframes stkfade{from{opacity:0}to{opacity:1}}@keyframes stkslide{from{transform:translateX(30px);opacity:.4}to{transform:translateX(0);opacity:1}}`}</style>
    </div>
  );
}
